import { useState, useEffect } from 'react';
import { MessageCircle, Plus, Clock, CheckCircle, AlertTriangle, ChevronRight, Send, X } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../utils/api';
import { formatDistanceToNow } from 'date-fns';

const STATUS_CFG = {
  open:         { color: '#F5A623', label: 'Open' },
  assigned:     { color: '#4DA2FF', label: 'Assigned' },
  in_progress:  { color: '#FF6B00', label: 'In Progress' },
  waiting_user: { color: '#9945FF', label: 'Awaiting Reply' },
  resolved:     { color: '#00C864', label: 'Resolved' },
  closed:       { color: '#6B6B8A', label: 'Closed' },
};

const CATEGORIES = [
  { value: 'order_issue', label: 'Order Issue' },
  { value: 'payment_problem', label: 'Payment Problem' },
  { value: 'account_issue', label: 'Account Issue' },
  { value: 'kyb_help', label: 'KYB Help' },
  { value: 'withdrawal_help', label: 'Withdrawal Help' },
  { value: 'product_issue', label: 'Product Issue' },
  { value: 'refund_request', label: 'Refund Request' },
  { value: 'other', label: 'Other' },
];

export default function SupportPage() {
  const [tickets, setTickets] = useState([]);
  const [selected, setSelected] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [reply, setReply] = useState('');
  const [form, setForm] = useState({ category: '', subject: '', message: '', priority: 'medium' });

  useEffect(() => { fetchTickets(); }, []);

  const fetchTickets = async () => {
    try {
      const res = await api.get('/support/tickets/my');
      setTickets(res.data.tickets || []);
    } catch { toast.error('Failed to load tickets'); }
    finally { setLoading(false); }
  };

  const openTicket = async (ticket) => {
    try {
      const res = await api.get(`/support/tickets/${ticket._id}`);
      setSelected(res.data.ticket);
    } catch { toast.error('Failed to open ticket'); }
  };

  const submitTicket = async () => {
    if (!form.category || !form.subject || !form.message) return toast.error('Fill in all fields');
    setSending(true);
    try {
      await api.post('/support/tickets', form);
      toast.success('Ticket submitted! We will respond within 24 hours.');
      setShowNew(false);
      setForm({ category: '', subject: '', message: '', priority: 'medium' });
      fetchTickets();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setSending(false); }
  };

  const sendReply = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      await api.post(`/support/tickets/${selected._id}/reply`, { message: reply });
      setReply('');
      openTicket(selected); // refresh
      toast.success('Reply sent');
    } catch { toast.error('Failed to send reply'); }
    finally { setSending(false); }
  };

  const rateTicket = async (rating) => {
    try {
      await api.post(`/support/tickets/${selected._id}/rate`, { rating });
      toast.success('Thank you for your feedback!');
      openTicket(selected);
    } catch { toast.error('Failed to rate'); }
  };

  // Ticket detail view
  if (selected) {
    const cfg = STATUS_CFG[selected.status] || STATUS_CFG.open;
    return (
      <div className="min-h-screen pt-20 pb-32 px-4 max-w-2xl mx-auto">
        <button onClick={() => setSelected(null)} className="flex items-center gap-1 text-brand-muted text-sm mt-4 mb-4 hover:text-white transition-colors">
          ← Back to tickets
        </button>

        <div className="glass rounded-2xl p-4 mb-4">
          <div className="flex items-start justify-between mb-2">
            <div>
              <p className="font-bold text-sm" style={{ fontFamily: 'Sora' }}>{selected.subject}</p>
              <p className="text-xs text-brand-muted font-mono">#{selected.ticketNumber}</p>
            </div>
            <span className="badge text-xs flex-shrink-0" style={{ background: `${cfg.color}15`, color: cfg.color, border: `1px solid ${cfg.color}30` }}>
              {cfg.label}
            </span>
          </div>
          {selected.assignedTo && (
            <p className="text-xs text-brand-muted">Assigned to: <strong className="text-brand-text">{selected.assignedTo.username}</strong></p>
          )}
        </div>

        {/* Messages */}
        <div className="space-y-3 mb-4">
          {selected.messages?.map((msg, i) => {
            const isUser = msg.senderRole === 'buyer' || msg.senderRole === 'seller';
            return (
              <div key={i} className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
                <div className="max-w-[85%] p-3 rounded-2xl text-sm" style={{
                  background: isUser ? 'linear-gradient(135deg,#FF6B00,#F5A623)' : '#12121A',
                  color: isUser ? 'white' : '#E0E0F0',
                  border: isUser ? 'none' : '1px solid #1E1E2E',
                  borderTopRightRadius: isUser ? 4 : 16,
                  borderTopLeftRadius: isUser ? 16 : 4,
                }}>
                  {!isUser && (
                    <p className="text-xs font-bold mb-1" style={{ color: '#FF6B00' }}>
                      {msg.senderRole === 'cs_staff' ? '💬 Support Staff' : '🛡️ Admin'}
                    </p>
                  )}
                  <p className="leading-relaxed">{msg.message}</p>
                  <p className={`text-xs mt-1 ${isUser ? 'text-white/60' : 'text-brand-muted'}`}>
                    {msg.createdAt ? formatDistanceToNow(new Date(msg.createdAt), { addSuffix: true }) : ''}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Rating if resolved */}
        {selected.status === 'resolved' && !selected.satisfactionRating && (
          <div className="p-4 rounded-2xl mb-4 text-center" style={{ background: '#12121A', border: '1px solid #1E1E2E' }}>
            <p className="text-sm font-semibold mb-3">How was your support experience?</p>
            <div className="flex justify-center gap-3">
              {[1,2,3,4,5].map(r => (
                <button key={r} onClick={() => rateTicket(r)}
                  className="w-10 h-10 rounded-full font-bold transition-all hover:scale-110"
                  style={{ background: r <= 3 ? 'rgba(255,50,50,0.1)' : 'rgba(0,200,100,0.1)', color: r <= 3 ? '#FF3232' : '#00C864', border: `1px solid ${r <= 3 ? 'rgba(255,50,50,0.3)' : 'rgba(0,200,100,0.3)'}` }}>
                  {['😤','😕','😐','😊','😍'][r-1]}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Reply box */}
        {!['resolved','closed'].includes(selected.status) && (
          <div className="fixed bottom-16 left-0 right-0 px-4 pb-4 max-w-2xl mx-auto" style={{ background: 'linear-gradient(transparent, #0A0A0F 30%)' }}>
            <div className="flex gap-2 p-2 rounded-2xl" style={{ background: '#12121A', border: '1px solid #1E1E2E' }}>
              <textarea value={reply} onChange={e => setReply(e.target.value)}
                placeholder="Type your reply..."
                className="flex-1 bg-transparent text-brand-text text-sm resize-none outline-none p-2 min-h-[44px] max-h-32"
                rows={1} />
              <button onClick={sendReply} disabled={!reply.trim() || sending}
                className="btn-primary px-4 py-2 flex-shrink-0 flex items-center gap-1 text-sm disabled:opacity-40">
                {sending ? <div className="spinner" /> : <><Send size={14} /> Send</>}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 pb-24 px-4 max-w-2xl mx-auto">
      <div className="flex items-center justify-between mt-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Sora' }}>Support</h1>
          <p className="text-xs text-brand-muted">We typically respond within 24 hours</p>
        </div>
        <button onClick={() => setShowNew(true)} className="btn-primary py-2 px-4 flex items-center gap-1.5 text-sm">
          <Plus size={15} /> New Ticket
        </button>
      </div>

      {/* New ticket form */}
      {showNew && (
        <div className="glass rounded-2xl p-4 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-bold" style={{ fontFamily: 'Sora' }}>New Support Ticket</h2>
            <button onClick={() => setShowNew(false)} className="text-brand-muted"><X size={18} /></button>
          </div>
          <div className="space-y-3">
            <select className="input-dark" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}>
              <option value="">Select category *</option>
              {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
            <input className="input-dark" placeholder="Subject *" value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} />
            <select className="input-dark" value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })}>
              <option value="low">Low Priority</option>
              <option value="medium">Medium Priority</option>
              <option value="high">High Priority</option>
              <option value="urgent">Urgent</option>
            </select>
            <textarea className="input-dark min-h-[100px] resize-none" placeholder="Describe your issue in detail *"
              value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} />
            <button onClick={submitTicket} disabled={sending} className="btn-primary w-full flex items-center justify-center gap-2">
              {sending ? <div className="spinner" /> : '📨 Submit Ticket'}
            </button>
          </div>
        </div>
      )}

      {/* Tickets list */}
      {loading ? (
        <div className="flex justify-center py-16"><div className="spinner" /></div>
      ) : tickets.length === 0 ? (
        <div className="text-center py-20">
          <MessageCircle size={48} className="text-brand-muted mx-auto mb-4 opacity-30" />
          <h3 className="text-lg font-bold mb-2" style={{ fontFamily: 'Sora' }}>No tickets yet</h3>
          <p className="text-brand-muted mb-4">Need help? Create a support ticket</p>
          <button onClick={() => setShowNew(true)} className="btn-primary px-8">Get Help</button>
        </div>
      ) : (
        <div className="space-y-3">
          {tickets.map(ticket => {
            const cfg = STATUS_CFG[ticket.status] || STATUS_CFG.open;
            return (
              <button key={ticket._id} onClick={() => openTicket(ticket)}
                className="w-full text-left p-4 rounded-2xl transition-all hover:border-brand-orange"
                style={{ background: '#12121A', border: '1px solid #1E1E2E' }}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm truncate">{ticket.subject}</p>
                    <p className="text-xs text-brand-muted font-mono mt-0.5">#{ticket.ticketNumber}</p>
                    <p className="text-xs text-brand-muted mt-1 capitalize">{ticket.category?.replace('_', ' ')}</p>
                  </div>
                  <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-full"
                      style={{ background: `${cfg.color}15`, color: cfg.color }}>
                      {cfg.label}
                    </span>
                    <span className="text-xs text-brand-muted">
                      {formatDistanceToNow(new Date(ticket.createdAt), { addSuffix: true })}
                    </span>
                  </div>
                </div>
                <div className="flex items-center justify-between mt-2">
                  <span className="text-xs text-brand-muted">{ticket.messages?.length || 0} messages</span>
                  <ChevronRight size={14} className="text-brand-muted" />
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
